import React from 'react';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import Tooltip from '../../elements/mui/Tooltip';
import Constants from '../../../Constants';
import UIUtils from '../../../lib/UIUtils';
import { ROUTE_DESTINATIONS, ROUTE_PIPELINES } from '../../../constants/pathnames';
import {
  PIPELINE_ACTION_ACTIVATE,
  PIPELINE_ACTION_PAUSE,
  DELETE_DISABLED_MESSAGES,
} from '../../../constants/pipelineConstants';
import Menu from '../../../components/elements/menu/Menu';
import RoutePrefixContext from '../../../../new/contexts/RoutePrefixContext';
import { ReactComponent as PauseIcon } from 'new/components/icons/pause.svg';
import { ReactComponent as ActivateIcon } from 'new/components/icons/bolt.svg';
import { ReactComponent as EditFileIcon } from 'new/components/icons/editFile.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as DuplicateIcon } from 'new/components/icons/copyAlt.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-info.svg';
import { restrictMenuForRole } from '../../../lib/ACLUtils';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import { isCapsuleCluster, isSingleDestinationChainFlow } from 'js/lib/PipelineUtils';
import { editModalsEnabled } from 'new/constants/featureFlags';
import { createActionValidator, createDeleteActionValidator } from 'js/lib/flowValidationUtils';

class DataSinkDetailActions extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this._handlePauseClicked = this._handlePauseClicked.bind(this);
    this._handleActivateClicked = this._handleActivateClicked.bind(this);
    this._handleDeleteClicked = this._handleDeleteClicked.bind(this);
    this._handleCopyClicked = this._handleCopyClicked.bind(this);
    this._handlePausePipelineClicked = this._handlePausePipelineClicked.bind(this);
    this._handleActivatePipelineClicked = this._handleActivatePipelineClicked.bind(this);
    this._handleOpenDetails = this._handleOpenDetails.bind(this);
  }

  render() {
    const {
      data,
      flow,
      readOnly,
      showStatus,
      inPipeline,
      isActivating,
      isPausing,
      selectActions,
      isCopying,
      isDeleting,
      hideMenuDetails,
      isManaged,
    } = this.props;
    const connectionCategory = null;
    const disableCopy = UIUtils.disallowSourceCopy(connectionCategory);
    const isCapsuleDestination = isCapsuleCluster(data);
    let statusClass = null;
    let statusName = null;
    let isEditable = !isManaged;
    const managedEditDisableMessage = 'This destination is managed by Nexla.';
    let menuItems = [
      {
        label: 'Destination',
        type: 'heading',
      },
    ];

    if (!hideMenuDetails) {
      menuItems.push({
        label: 'Details',
        icon: <DetailsIcon />,
        onClick: this._handleOpenDetails,
      });
    }

    if (data.node_type === 'llm_processor') return null;

    const actionsValidationForFlow = createActionValidator(flow ?? data, isPausing, isActivating);

    switch (data.status) {
      case Constants.SourceStatus.ACTIVE:
        statusClass = 'status-active';
        statusName = 'Active';
        isEditable = isCapsuleDestination && !isManaged;
        if (!isCapsuleDestination && (!selectActions || selectActions.includes('activate'))) {
          menuItems.push({
            label: isPausing ? 'Pausing...' : 'Pause',
            icon: <PauseIcon />,
            onClick: this._handlePauseClicked,
            ...actionsValidationForFlow,
          });
        }
        break;

      case Constants.SourceStatus.PAUSED:
        statusClass = 'status-paused';
        statusName = 'Paused';
        if (!isCapsuleDestination && (!selectActions || selectActions.includes('activate'))) {
          menuItems.push({
            label: isActivating ? 'Activating...' : 'Activate',
            icon: <ActivateIcon />,
            onClick: this._handleActivateClicked,
            ...actionsValidationForFlow,
          });
        }
        break;

      case Constants.SourceStatus.RATE_LIMITED:
        statusClass = 'status-rate-limited';
        statusName = 'Billing Tier Limit Exceeded';
        break;

      default:
        statusClass = 'status-init';
        statusName = 'Initialized';
        if (!isCapsuleDestination && (!selectActions || selectActions.includes('activate'))) {
          menuItems.push({
            label: isActivating ? 'Activating...' : 'Activate',
            icon: <ActivateIcon />,
            disable: isActivating,
            onClick: this._handleActivateClicked,
            ...actionsValidationForFlow,
          });
        }
    }

    const flowType = data?.flow_type || flow?.flow_type;

    const editHref = {
      in_memory: `${this.context}${ROUTE_PIPELINES}/in_memory/sink/${data.id}/edit`,
      custom: `${this.context}${ROUTE_PIPELINES}/custom/sink/${data.id}/edit`,
      replication: `${this.context}${ROUTE_PIPELINES}/replication/sink/${data.id}/edit`,
      spark: `${this.context}${ROUTE_PIPELINES}/spark/sink/${data.id}/edit`,
      cdc: `${this.context}${ROUTE_PIPELINES}/cdc/sink/${data.id}/edit`,
      elt: `${this.context}${ROUTE_PIPELINES}/elt/sink/${data.id}/edit`,
      streaming: `${this.context}${ROUTE_DESTINATIONS}/${data.id}/edit`,
    };

    const isModalEnabled = editModalsEnabled()?.sink?.[flowType];

    const editMenuItem = {
      label: 'Edit',
      icon: <EditFileIcon />,
      href: !isModalEnabled ? editHref[flowType] || editHref.streaming : undefined,
      onClick: isModalEnabled ? this._handleEditClicked : undefined,
    };

    // Apply delete validation using new standard
    const deleteValidation = createDeleteActionValidator(
      data.status,
      isDeleting,
      isPausing,
      DELETE_DISABLED_MESSAGES.DESTINATION,
    );

    const deleteMenuItem = {
      type: 'dialog',
      label: isDeleting ? 'Deleting...' : 'Delete',
      icon: <DeleteIcon />,
      color: 'error',
      dialogMessage: `Please confirm you want to delete destination ${data.name}(ID: ${data.id})`,
      primaryButtonColor: 'error',
      discardLabel: 'Cancel',
      confirmLabel: 'Delete',
      onClick: this._handleDeleteClicked,
      ...deleteValidation,
    };

    if (!isEditable) {
      editMenuItem.disable = true;
      editMenuItem.disableMessage = isManaged
        ? managedEditDisableMessage
        : 'Active resources cannot be edited. Please Pause to edit.';
    }

    if (!selectActions || selectActions.includes('edit')) {
      menuItems.push(editMenuItem);
    }

    if (!disableCopy) {
      if (!selectActions || selectActions.includes('duplicate')) {
        const singleDestinationFlow = isSingleDestinationChainFlow(flowType);
        const capsuleFlow = isCapsuleCluster(flow);

        const disableMessage =
          singleDestinationFlow || capsuleFlow
            ? 'This flow can only have one destination. Please duplicate the flow instead.'
            : isCopying
              ? 'This destination is already being duplicated.'
              : '';
        menuItems.push({
          label: isCopying ? 'Duplicating...' : 'Duplicate',
          icon: <DuplicateIcon />,
          disable: isCopying || singleDestinationFlow || capsuleFlow,
          onClick: this._handleCopyClicked,
          disableMessage: disableMessage,
        });
      }
    }

    if (!selectActions || selectActions.includes('delete')) {
      menuItems.push(deleteMenuItem);
    }

    menuItems.push(
      {
        label: 'Flow',
        type: 'heading',
      },
      {
        label: 'Activate Flow',
        icon: <ActivateIcon />,
        onClick: this._handleActivatePipelineClicked,
        ...actionsValidationForFlow,
      },
      {
        label: 'Pause Flow',
        icon: <PauseIcon />,
        onClick: this._handlePausePipelineClicked,
        ...actionsValidationForFlow,
      },
    );

    const statusImageClassName = statusClass
      ? classnames('status-image-placeholder', statusClass)
      : null;
    const statusView =
      showStatus && statusImageClassName ? (
        <Tooltip title={statusName} arrow={true}>
          <div className="status-holder">
            <div className={statusImageClassName}>
              <div className="bolt" />
            </div>
          </div>
        </Tooltip>
      ) : null;

    menuItems = restrictMenuForRole(menuItems, data);

    if (this.props.additionalMenuItems) {
      this.props.additionalMenuItems.forEach((item) => {
        if (item.position === 'bottom') {
          menuItems.push(item);
        } else {
          menuItems.unshift(item);
        }
      });
    }

    return (
      <div data-testid={`data-sink-detail-actions-${data.id}`} className="detail-actions">
        {statusView}
        {!readOnly && (
          <Menu
            menuButtonClassName={this.props.menuButtonClassName}
            horizontalDots={this.props.horizontalDots}
            items={menuItems}
            inPipeline={inPipeline}
          />
        )}
      </div>
    );
  }

  _handlePauseClicked(event) {
    this.props.pauseSink(this.props.data.id);
  }

  _handleActivateClicked(event) {
    this.props.activateSink(this.props.data.id);
  }

  _handleDeleteClicked(event) {
    this.props.deleteSink(this.props.data.id);
  }

  _handleCopyClicked(event) {
    this.props.copySink(this.props.data.id);
  }

  _handlePausePipelineClicked() {
    const isCapsuleDestination = isCapsuleCluster(this.props.data);
    if (isCapsuleDestination) {
      this.props.setPipelineAction(this.props.data.origin_node_id, PIPELINE_ACTION_PAUSE);
      return;
    }

    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_PAUSE);
  }

  _handleActivatePipelineClicked() {
    const isCapsule = isCapsuleCluster(this.props.data);
    if (isCapsule) {
      this.props.setPipelineAction(this.props.data.origin_node_id, PIPELINE_ACTION_ACTIVATE);
      return;
    }
    this.props.setPipelineAction(this.props.data.flow_node_id, PIPELINE_ACTION_ACTIVATE);
  }

  _handleOpenDetails(e) {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    const { data, onOpenDetailsMenu, openDetailDrawerInPortal, openDetailsModal } = this.props;
    if (typeof onOpenDetailsMenu === 'function') {
      onOpenDetailsMenu(data);
      return;
    }
    if (typeof openDetailsModal === 'function') {
      openDetailsModal(data);
      return;
    }
    if (typeof openDetailDrawerInPortal === 'function') {
      openDetailDrawerInPortal(data, {});
    }
  }

  _handleEditClicked = () => {
    const { data, flow } = this.props;
    const flowType = data.flow_type || flow?.flow_type;
    this.props.setAction({
      type: 'edit',
      flowType,
      resource: RESOURCE_TYPE_SINK,
      id: data.id,
    });
  };
}

DataSinkDetailActions.defaultProps = {
  allowDeletion: true,
  showStatus: false,
  isManaged: false,
  hideMenuDetails: false,
};

export default withRouter(DataSinkDetailActions);
